import isObject from 'lodash/isObject';

import type {InsightsSeriesTree, MetricMetadata} from 'common/report/insights/util/types';
import {getValueIfLeaf} from 'common/report/insights/util/series';
import {RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {CommonDataTableFields} from 'common/components/data-table/types';

/**
 * $overall values for each segmentation level are not displayed in table
 * results, they are displayed as segment summaries for the different
 * metrics in the results; these control how multi-seg sorting
 * works, instead of constructing sum trees at each segment level
 * $overall at each segment level dictates sorting
 *
 * @example
 * // For the following API results, OverallResultsMap will contain `$event`, `US` and `Spain` as entries
 * // with their corresponding $overall values mapping to each metric in the results, the `$event` entry
 * // maps to unsegmented metric values for each of the metrics
 * [
 *  [`$event`, {'$mp-metric-0': 15, '$mp-metric-1': 20} ],
 *  [`US`, {'$mp-metric-0': 15}],
 *  [`Spain`, {'$mp-metric-1': 20}],
 * ]
 * {
 *   "Purchase": {
 *     "$overall": { "all": 15 },
 *     "US": {
 *       "$overall": { "all": 15 },
 *       "Firefox": { "all": 10 },
 *       "Chrome": { "all": 5 }
 *     }
 *   },
 *   "Watch Video": {
 *     "$overall": { "all": 20 },
 *     "Spain": {
 *       "$overall": { "all": 20 },
 *       "Firefox": { "all": 19 },
 *       "Chrome": { "all": 1 }
 *     }
 *   }
 * }
 */
export type OverallResultsMap = Map<string, Record<string, number>>;

/**
 * For an Insights distribution series where all results are enclosed in a `{all: <result>}` object, computes
 * all the metric names in the results and its value for each segment combination
 *
 * @see OverallResultsMap
 * @param resultsSeries
 * @example
 * pivotSeries({
 *     "Purchase": {
 *        "$overall": { "all": 30 },
 *        "United States": { "all": 30 },
 *     },
 *      "Watch Video": {
 *        "$overall": { "all": 15 },
 *        "United States": { "all": 15 },
 *     },
 * });
 * Returns
 * {
 *    metrics: {'$mp-metric-0': `Purchase`, '$mp-metric-1': `Watch Video`},
 *    overallResults: [ [`$event`, {'$mp-metric-0': 30, '$mp-metric-1': 15}] ]
 *    series: {
 *        "United States": {
 *          "Purchase": 30,
 *          "Watch Video": 15
 *        },
 *    }
 * }
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function pivotSeries(
  metricMetadata: MetricMetadata[],
  resultsSeries: InsightsSeriesTree,
): {metrics: Record<string, string>; series: InsightsSeriesTree; overallResults: OverallResultsMap} {
  const overallResults: OverallResultsMap = new Map();

  const metricKeys = metricMetadata
    .map((metadata) =>
      // expand and unnest $top_events sub_queries to pivot on all top metrics
      metadata.sub_queries ? metadata.sub_queries.map((subMetadata) => subMetadata.metric_key) : metadata.metric_key,
    )
    .flat();

  const metrics = Object.fromEntries(
    metricKeys.map((metricKey, index) => [CommonDataTableFields.MetricPrefix + index, metricKey]),
  );

  const _recordOverallValue = (path: string, value: number, metricId: string) => {
    // root $overall values are defined within the $event key
    const overallKey = path ?? `$event`;
    const overallResult = overallResults.get(overallKey) ?? {};
    overallResult[metricId] = value;
    overallResults.set(overallKey, overallResult);
  };

  const _traverseSeries = (
    subResult: InsightsSeriesTree,
    segmentsPaths: InsightsSeriesTree | number,
    metricId: string,
    path = null,
  ) => {
    // for cases where there's no segmentation and $overall results,
    // we still want to maintain the metric results in overallResult
    if (path === null && getValueIfLeaf(subResult) !== null) {
      _recordOverallValue(path, getValueIfLeaf(subResult), metricId);
    }

    for (const [key, value] of Object.entries(subResult)) {
      // $overall results are not displayed in a pivot table layout, they
      // become part of table summary fields and that metadata for displaying
      // overall values for each metric at different segmentation
      // levels is captured here
      if (key === RESULTS_OVERALL_SUM_KEY) {
        _recordOverallValue(path, getValueIfLeaf(value as InsightsSeriesTree), metricId);
      }

      if (isObject(value)) {
        segmentsPaths[key] = segmentsPaths[key] ?? {};
        _traverseSeries(value as InsightsSeriesTree, segmentsPaths[key], metricId, (path ? `${path},` : ``) + key);
      } else {
        segmentsPaths[metricId] = getValueIfLeaf(subResult) ?? Object.assign({}, subResult);
        break;
      }
    }
  };

  const series: InsightsSeriesTree = {};
  for (const metricId in metrics) {
    const metricName = metrics[metricId];

    // skip metrics yielding no results in the series
    if (metricName === null || resultsSeries[metricName] === undefined) {
      continue;
    }

    if (Number.isFinite(resultsSeries[metricName])) {
      throw new Error(`unexpected data`);
    }

    _traverseSeries(resultsSeries[metricName] as InsightsSeriesTree, series, metricId);
  }

  return {metrics, series, overallResults};
}
